import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { berlinDate, workBreakdown, type WorkEmployee, type WorkEntry, type WorkEntryList } from '@kontor/shared';
import { api, ApiError, queryString } from '../../api';
import { InfoHint } from '../../components/InfoHint';
import { useSession } from '../../session';
import { dayDe } from './PayrollPage';
import './payroll.css';

const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const weekday = (iso: string) => WEEKDAYS[new Date(`${iso}T12:00:00Z`).getUTCDay()]!;
const hm = (minutes: number) => `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
const MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const monthLabel = (m: string) => new Date(`${m}-15T12:00:00Z`).toLocaleDateString('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const monthEnd = (m: string) => new Date(Date.UTC(Number(m.slice(0, 4)), Number(m.slice(5, 7)), 0)).toISOString().slice(0, 10);
const shiftMonth = (m: string, by: number) => {
  const d = new Date(Date.UTC(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1 + by, 1));
  return d.toISOString().slice(0, 7);
};
const errorText = (e: unknown) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : e instanceof Error ? e.message : 'Das hat nicht geklappt.');

/**
 * Arbeitszeiten: Die Stunden der Mitarbeiter je Tag, von Hand erfasst. Wer keine Schichtplanung hat, bekommt daraus die Stunden für Stundenlöhne und die Zuschläge für Nacht und Sonntag
 * im Lohnlauf. Die Seite zeigt je Monat die Einträge, die Summen je Mitarbeiter gegen die vertraglichen Wochenstunden und Hinweise zu Pause und Ruhezeit.
 */
export function WorkTimePage() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const write = can('payroll.write');
  const [month, setMonth] = useState(berlinDate().slice(0, 7));
  const [employeeId, setEmployeeId] = useState('');
  const [editing, setEditing] = useState<WorkEntry | null>(null);

  const people = useQuery({ queryKey: ['work-employees'], queryFn: async () => (await api<{ data: WorkEmployee[] }>('GET', '/payroll/work-entries/employees')).data });
  const range = { from: `${month}-01`, to: monthEnd(month) };
  const thisYear = Number(berlinDate().slice(0, 4));
  const years = Array.from({ length: 6 }, (_, i) => String(thisYear - i));
  const list = useQuery({
    queryKey: ['work-entries', range, employeeId],
    queryFn: async () => (await api<{ data: WorkEntryList }>('GET', `/payroll/work-entries${queryString({ ...range, employeeId })}`)).data,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['work-entries'] });
    void queryClient.invalidateQueries({ queryKey: ['payroll-overview'] });
  };
  const remove = useMutation({ mutationFn: async (id: string) => api('DELETE', `/payroll/work-entries/${id}`), onSuccess: refresh });
  const d = list.data;

  return (
    <>
      <div className="page-head">
        <h1>Arbeitszeiten</h1>
        <span className="muted">{d ? `${d.entries.length} Einträge im ${monthLabel(month)}` : ''}</span>
      </div>
      <p className="payroll-notice">Tragen Sie hier ein, wann gearbeitet wurde. Der Lohnlauf übernimmt die Stunden für Stundenlöhne und die Zuschläge für Nacht und Sonntag. Wer eine Schichtplanung hat, braucht das nur für Abweichungen: Erfasste Zeiten gehen den geplanten Schichten vor.</p>

      <section className="card work-bar" aria-label="Zeitraum und Mitarbeiter">
        <div className="field work-bar__monthfield">
          <span>Monat</span>
          <div className="work-bar__month">
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="Voriger Monat">‹</button>
            <select className="input" value={month.slice(5, 7)} onChange={(e) => setMonth(`${month.slice(0, 4)}-${e.target.value}`)} aria-label="Monat">
              {MONTH_NAMES.map((n, i) => <option key={n} value={String(i + 1).padStart(2, '0')}>{n}</option>)}
            </select>
            <select className="input work-bar__year" value={month.slice(0, 4)} onChange={(e) => setMonth(`${e.target.value}-${month.slice(5, 7)}`)} aria-label="Jahr">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setMonth(shiftMonth(month, 1))} disabled={month >= berlinDate().slice(0, 7)} aria-label="Nächster Monat">›</button>
          </div>
        </div>
        <label className="field work-bar__person">
          <span>Mitarbeiter</span>
          <select className="input" value={employeeId} onChange={(e) => { setEmployeeId(e.target.value); setEditing(null); }}>
            <option value="">Alle Mitarbeiter</option>
            {(people.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      </section>

      {write && <EntryForm key={editing?.id ?? 'new'} people={people.data ?? []} defaultEmployee={employeeId} editing={editing} month={month} onDone={() => { setEditing(null); refresh(); }} onCancel={() => setEditing(null)} />}

      {list.isError && <div className="alert alert--error" role="alert">{errorText(list.error)}</div>}

      {d && d.totals.length > 0 && (
        <div className="work-totals">
          {d.totals.map((t) => {
            const diff = t.targetMinutes === null ? null : t.minutes - t.targetMinutes;
            return (
              <div key={t.employeeId ?? t.employeeName} className="card work-total">
                <strong>{t.employeeName}</strong>
                <span className="work-total__main">{hm(t.minutes)} Std.</span>
                <span className="muted small">{t.days} Arbeitstage{t.nightMinutes > 0 && <>, Nacht {hm(t.nightMinutes)}</>}{t.sundayMinutes > 0 && <>, Sonntag {hm(t.sundayMinutes)}</>}</span>
                {diff !== null && <span className={`small ${diff < 0 ? 'muted' : ''}`}>Soll {hm(t.targetMinutes!)} Std. ({diff >= 0 ? '+' : '−'}{hm(Math.abs(diff))})</span>}
              </div>
            );
          })}
        </div>
      )}

      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <th>Datum</th>
              {!employeeId && <th>Mitarbeiter</th>}
              <th>Zeit</th>
              <th className="num">Pause</th>
              <th className="num">Arbeitszeit</th>
              <th className="num">Nacht</th>
              <th className="num">Sonntag</th>
              <th>Hinweis</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={9} className="empty">Wird geladen …</td></tr>}
            {d && d.entries.length === 0 && <tr><td colSpan={9} className="empty">Keine Arbeitszeiten in diesem Monat.</td></tr>}
            {(d?.entries ?? []).map((e) => (
              <tr key={e.id} className={editing?.id === e.id ? 'row--selected' : undefined}>
                <td data-label="Datum">{weekday(e.workDate)}, {dayDe(e.workDate)}</td>
                {!employeeId && <td data-label="Mitarbeiter">{e.employeeName}</td>}
                <td data-label="Zeit">{e.startTime} – {e.endTime}{e.overnight && <span className="muted small"> (bis Folgetag)</span>}</td>
                <td data-label="Pause" className="num">{e.breakMinutes} Min.</td>
                <td data-label="Arbeitszeit" className="num"><strong>{hm(e.minutes)}</strong></td>
                <td data-label="Nacht" className="num">{e.nightMinutes ? hm(e.nightMinutes) : '–'}</td>
                <td data-label="Sonntag" className="num">{e.sundayMinutes ? hm(e.sundayMinutes) : '–'}</td>
                <td data-label="Hinweis" className="small">
                  {e.locked ? <span className="badge">im Lohnlauf festgeschrieben</span> : e.warnings.length > 0 ? <span className="work-warn">{e.warnings.join(' ')}</span> : e.note ? <span className="muted">{e.note}</span> : '–'}
                </td>
                <td className="work-actions">
                  {write && !e.locked && <button type="button" className="link-button" onClick={() => { setEditing(e); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Ändern</button>}
                  {can('payroll.delete') && !e.locked && (
                    <button type="button" className="link-button" disabled={remove.isPending} onClick={() => window.confirm(`Eintrag vom ${dayDe(e.workDate)} (${e.employeeName}) löschen?`) && remove.mutate(e.id)}>Löschen</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function EntryForm({ people, defaultEmployee, editing, month, onDone, onCancel }: { people: WorkEmployee[]; defaultEmployee: string; editing: WorkEntry | null; month: string; onDone: () => void; onCancel: () => void }) {
  const today = berlinDate();
  const [employee, setEmployee] = useState(editing?.employeeId ?? defaultEmployee);
  const [date, setDate] = useState(editing?.workDate ?? (month === today.slice(0, 7) ? today : `${month}-01`));
  const [start, setStart] = useState(editing?.startTime ?? '08:00');
  const [end, setEnd] = useState(editing?.endTime ?? '16:00');
  const [pause, setPause] = useState(String(editing?.breakMinutes ?? 30));
  const [note, setNote] = useState(editing?.note ?? '');
  const [error, setError] = useState<string | null>(null);

  // Wechselt oben der Mitarbeiter, folgt das Formular, solange kein Eintrag bearbeitet wird
  useEffect(() => {
    if (!editing && defaultEmployee) setEmployee(defaultEmployee);
  }, [defaultEmployee, editing]);

  const minutes = Number(pause);
  const preview = useMemo(() => {
    if (!date || !start || !end || start === end || !Number.isInteger(minutes) || minutes < 0) return null;
    return workBreakdown({ workDate: date, startTime: start, endTime: end, breakMinutes: minutes });
  }, [date, start, end, minutes]);

  const save = useMutation({
    mutationFn: async () => {
      if (!editing && !employee) throw new Error('Bitte einen Mitarbeiter wählen.');
      if (!date) throw new Error('Bitte ein Datum angeben.');
      if (!start || !end) throw new Error('Bitte Beginn und Ende angeben.');
      if (start === end) throw new Error('Beginn und Ende sind gleich.');
      if (!Number.isInteger(minutes) || minutes < 0) throw new Error('Bitte die Pause in Minuten angeben.');
      return editing
        ? api('PATCH', `/payroll/work-entries/${editing.id}`, { workDate: date, startTime: start, endTime: end, breakMinutes: minutes, note: note.trim() || null })
        : api('POST', '/payroll/work-entries', { employeeId: employee, workDate: date, startTime: start, endTime: end, breakMinutes: minutes, note: note.trim() || null });
    },
    onSuccess: () => { setError(null); setNote(''); onDone(); },
    onError: (e) => setError(errorText(e)),
  });

  return (
    <form className="card work-form" noValidate onSubmit={(e) => { e.preventDefault(); save.mutate(); }} aria-label={editing ? 'Eintrag ändern' : 'Arbeitszeit erfassen'}>
      <h2>{editing ? `Eintrag ändern: ${editing.employeeName}` : 'Arbeitszeit erfassen'}</h2>
      <div className="work-form__grid">
        {!editing && (
          <label className="field">
            <span>Mitarbeiter</span>
            <select className="input" value={employee} onChange={(e) => setEmployee(e.target.value)}>
              <option value="">Bitte wählen</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        )}
        <label className="field"><span>Datum</span><input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label className="field"><span>Beginn</span><input className="input" type="time" value={start} onChange={(e) => setStart(e.target.value)} /></label>
        <label className="field"><span>Ende<InfoHint label="Ende">Liegt das Ende vor dem Beginn, reicht die Arbeit über Mitternacht in den nächsten Tag, zum Beispiel 22:00 bis 06:00. Der Eintrag gehört zum Tag des Beginns.</InfoHint></span><input className="input" type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
        <label className="field"><span>Pause (Minuten)<InfoHint label="Pause">Ab mehr als sechs Stunden sind 30 Minuten Pause vorgeschrieben, ab mehr als neun Stunden 45 Minuten. Die Pause zählt nicht zur bezahlten Arbeitszeit.</InfoHint></span><input className="input" type="number" inputMode="numeric" min={0} max={600} value={pause} onChange={(e) => setPause(e.target.value)} /></label>
        <label className="field work-form__note"><span>Notiz</span><input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder="optional" /></label>
        <div className="work-form__actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : editing ? 'Speichern' : 'Erfassen'}</button>
          {editing && <button className="btn btn--secondary" type="button" onClick={onCancel}>Abbrechen</button>}
        </div>
      </div>
      {preview && (
        <p className="muted small work-form__preview">
          {hm(preview.minutes)} Std. Arbeitszeit{preview.overnight && ', über Mitternacht'}{preview.nightMinutes > 0 && `, davon ${hm(preview.nightMinutes)} in der Nacht`}{preview.sundayMinutes > 0 && `, davon ${hm(preview.sundayMinutes)} am Sonntag`}.
        </p>
      )}
      {error && <div className="alert alert--error" role="alert">{error}</div>}
    </form>
  );
}
